<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import { useTemplateRefsList } from '@vueuse/core';

import { defaultIncomingProperty, defaultSupportingProperty } from '@/helpers/builder';
import { PropertyDetailOption, ErhlLoanPurpose } from '@/helpers/const';
import { requiresSupportingProperty as purchasePropertiesRequireSupporting } from '@/helpers/erhlPropertyRequirements';
import api from '@/api';
import { useApplicationApi } from '@/helpers/useApplicationApi';
import { INVALID_JWT_ERROR, useSessionExpiry } from '@/helpers/useSessionExpiry';
import { useErhlLoanAppProcess } from '@/helpers/useErhlLoanAppProcess';
import { PlusCircleIcon } from '@heroicons/vue/20/solid';

import PropertyDetailCard from './modules/PropertyDetails/PropertyDetailCard.vue';

import type { PropertyDetailEntry, IncomingProperty, SupportingProperty } from '@/types';

const { saveApplicationToDraft } = useApplicationApi();
const { loanApp, state, saveAndGoNext, goBack } = useErhlLoanAppProcess();
const { flagSessionExpired } = useSessionExpiry();

const isPurchaseNewProperty = computed(() => {
  const purposes = loanApp.value.erhlLoanPurposes ?? [];
  return Array.isArray(purposes) && purposes.includes(ErhlLoanPurpose.PURCHASE_NEW_PROPERTY);
});

// ─── Purchase properties (incoming) ────────────────────────────────
const loadExistingPurchaseProperties = (): { item: PropertyDetailEntry }[] =>
  loanApp.value.incomingProperties.map((p) => ({
    item: { ...p, propertyOptions: PropertyDetailOption.PROPERTY_TO_PURCHASE },
  }));

const purchaseProperties = ref<{ item: PropertyDetailEntry }[]>(loadExistingPurchaseProperties());
const purchaseRefs = useTemplateRefsList<InstanceType<typeof PropertyDetailCard>>();
const purchaseCounter = ref(purchaseProperties.value.length);

const generatePurchaseId = () => `purchase-property-${purchaseCounter.value++}`;

const removePurchaseProperty = (id: string) => {
  purchaseProperties.value = purchaseProperties.value.filter((p) => p.item.id !== id);
};

const getPurchasePropertyCount = (index: number) => index + 1;

// A retirement, land-lease or community home purchase property isn't taken as security (JS-9416),
// so at least one supporting property is required, and it can't be removed while this holds.
const requiresSupportingProperty = computed(() =>
  purchasePropertiesRequireSupporting(purchaseProperties.value.map((p) => p.item)),
);

// ─── Supporting properties ─────────────────────────────────────────
const loadExistingSupportingProperties = (): { item: PropertyDetailEntry }[] =>
  (loanApp.value.supportingProperties ?? []).map((p) => ({
    item: { ...p, propertyOptions: PropertyDetailOption.SUPPORTING_PROPERTY },
  }));

const supportingProperties = ref<{ item: PropertyDetailEntry }[]>(loadExistingSupportingProperties());
const supportingRefs = useTemplateRefsList<InstanceType<typeof PropertyDetailCard>>();
const supportingCounter = ref(supportingProperties.value.length);

const generateSupportingId = () => `supporting-property-${supportingCounter.value++}`;

/** Adds a new supporting property card after validating existing ones. */
const addSupportingProperty = async () => {
  if (await validateSupportingItems()) {
    supportingProperties.value.push({ item: { id: generateSupportingId() } });
  }
};

const removeSupportingProperty = (id: string) => {
  supportingProperties.value = supportingProperties.value.filter((p) => p.item.id !== id);
};

const getSupportingPropertyCount = (index: number) => index + 1;

// The auto-added supporting property required by a retirement/land-lease/community purchase
// can't be removed; any additional supporting properties beyond that one still can be.
const canRemoveSupportingProperty = computed(
  () => !requiresSupportingProperty.value || supportingProperties.value.length > 1,
);

// ─── Validation ────────────────────────────────────────────────────
const isLoading = ref(false);
const validationError = ref('');

const validatePurchaseItems = async () => {
  const results = await Promise.all(purchaseRefs.value.map((ref) => ref.update()));
  return results.every(Boolean);
};

const validateSupportingItems = async () => {
  const results = await Promise.all(supportingRefs.value.map((ref) => ref.update()));
  return results.every(Boolean);
};

/**
 * Validates all property cards and checks ERHL-specific requirements:
 * - When "Purchase new property" is selected: at least one purchase property is required;
 *   any added supporting properties must also be valid.
 * - Otherwise: at least one supporting property is required.
 */
const validateAll = async (): Promise<boolean> => {
  validationError.value = '';

  if (isPurchaseNewProperty.value) {
    if (purchaseProperties.value.length === 0) {
      validationError.value = 'You must add at least one purchase property.';
      return false;
    }
    const purchaseValid = await validatePurchaseItems();
    if (!purchaseValid) return false;
    if (requiresSupportingProperty.value && supportingProperties.value.length === 0) {
      validationError.value =
        'A supporting property is required when purchasing a retirement, land-lease or community home.';
      return false;
    }
    return validateSupportingItems();
  }

  if (supportingProperties.value.length === 0) {
    validationError.value = 'You must add at least one supporting property.';
    return false;
  }
  return validateSupportingItems();
};

// ─── Save ──────────────────────────────────────────────────────────
const saveAndContinue = async () => {
  if (!(await validateAll())) return;

  isLoading.value = true;

  const incomingProperties: IncomingProperty[] = isPurchaseNewProperty.value
    ? purchaseProperties.value.map((p) => ({ ...defaultIncomingProperty(), ...p.item }))
    : [];

  const supportingProps: SupportingProperty[] = supportingProperties.value.map((p) => ({
    ...defaultSupportingProperty(),
    ...p.item,
  }));

  try {
    let applicationDraftId = loanApp.value.applicationDraftId;
    if (loanApp.value.brokerId) {
      const savedDraftId = await saveApplicationToDraft(state.value.context, {
        ...loanApp.value,
        incomingProperties,
        outgoingProperties: [],
        supportingProperties: supportingProps,
      });
      applicationDraftId = savedDraftId ?? applicationDraftId;
    }

    saveAndGoNext({
      incomingProperties,
      outgoingProperties: [],
      supportingProperties: supportingProps,
      applicationDraftId,
    });
  } catch (error) {
    if (api.getErrorMsg(error) === INVALID_JWT_ERROR) {
      flagSessionExpired();
      return;
    }
    console.error('Failed to save draft:', error);
  } finally {
    isLoading.value = false;
  }
};

// Seed one supporting property if none exist and one is required: either because
// "Purchase new property" is not selected (a supporting property is the sole security), or
// because a purchase property is a retirement, land-lease or community home, which isn't taken
// as security and so needs a supporting property as backup security.
watch(
  [isPurchaseNewProperty, requiresSupportingProperty, supportingProperties],
  ([isPurchase, requiresSupporting, val]) => {
    if ((!isPurchase || requiresSupporting) && val.length === 0) {
      supportingProperties.value.push({ item: { id: generateSupportingId() } });
    }
  },
  { immediate: true },
);

// Seed one purchase property if none exist when purchase purpose is active
watch(
  [isPurchaseNewProperty, purchaseProperties],
  ([isPurchase, properties]) => {
    if (isPurchase && properties.length === 0) {
      purchaseProperties.value.push({ item: { id: generatePurchaseId() } });
    }
  },
  { immediate: true },
);
</script>

<template>
  <div class="w-full">
    <div class="mb-8 pb-2">
      <h2 class="text-2xl font-medium">
        Tell us about the
        <BxHighlight> property</BxHighlight>
      </h2>
      <p v-if="!isPurchaseNewProperty" class="mt-1 text-sm text-gray-500">
        This property (or properties) will be used as security for the loan.
      </p>
    </div>

    <!-- Property section for purchase mode: 1 purchase + optional supporting properties -->
    <div v-if="isPurchaseNewProperty" class="section-panel md:p-6">
      <div class="mb-6">
        <h3 class="hidden text-lg font-medium leading-6 text-gray-900 sm:block">Property</h3>
      </div>
      <template v-for="(property, index) in purchaseProperties" :key="property?.item?.id">
        <PropertyDetailCard
          :ref="purchaseRefs.set"
          v-model="property.item"
          property-mode="purchase"
          :index="getPurchasePropertyCount(index)"
          class="mb-4"
          is-multiple
          @remove="removePurchaseProperty"
          @saved="() => {}"
        />
      </template>
      <template v-for="(property, index) in supportingProperties" :key="property?.item?.id">
        <PropertyDetailCard
          :ref="supportingRefs.set"
          v-model="property.item"
          property-mode="supporting"
          :index="getSupportingPropertyCount(index)"
          class="mb-4"
          is-multiple
          :removable="canRemoveSupportingProperty"
          @remove="removeSupportingProperty"
          @saved="() => {}"
        />
      </template>
      <div class="mt-6 flex justify-end">
        <BxButton id="purchase-property-add-supporting" variant="tertiary" @click="addSupportingProperty()">
          <PlusCircleIcon class="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
          Add another property
        </BxButton>
      </div>
    </div>

    <!-- Supporting property section (when "Purchase new property" is NOT selected) -->
    <div v-else class="section-panel md:p-6">
      <div class="mb-6">
        <h3 class="hidden text-lg font-medium leading-6 text-gray-900 sm:block">Property</h3>
      </div>
      <template v-for="(property, index) in supportingProperties" :key="property?.item?.id">
        <PropertyDetailCard
          :ref="supportingRefs.set"
          v-model="property.item"
          property-mode="supporting"
          :index="getSupportingPropertyCount(index)"
          class="mb-4"
          is-multiple
          @remove="removeSupportingProperty"
          @saved="() => {}"
        />
      </template>
      <div class="mt-6 flex justify-end">
        <BxButton id="supporting-property-add" variant="tertiary" @click="addSupportingProperty()">
          <PlusCircleIcon class="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
          Add another property
        </BxButton>
      </div>
    </div>

    <BxErrorPanel v-if="validationError" class="mt-6">
      {{ validationError }}
    </BxErrorPanel>

    <div class="mt-6">
      <BxButton id="property-details-continue" variant="primary" :loading="isLoading" @click="saveAndContinue()">
        Save and continue
      </BxButton>
      <BxButton v-if="state.nextEvents.includes('BACK')" variant="link" class="ml-4" @click="goBack()">Back</BxButton>
    </div>
  </div>
</template>
